/**
 * Card authorisation list of a Teltonika unit (ADR 0032). The unit keeps the list in parameter slots
 * (supplier format: parameter 30000 + slot, value = card id; 0 = empty slot; 4000 slots). The cloud decides
 * WHAT the unit should hold (authlist_entries, ADR 0009); this module decides HOW: which slots to write so
 * that only the difference to the last confirmed state is sent, and how to split it into short commands.
 *
 * Command guard: the only Codec 12 commands FTS may send are read-only queries (incl. `getparam` of the card
 * slots and of the scenario output-control parameters) and `setparam` on the card slots. `setdigout` (drives
 * a digital output, e.g. the immobiliser relay) and every other parameter are refused, so no code path can
 * act on the vehicle — in particular never because of an impact (ADR 0003).
 */
import { cardUid } from "../icd/schemas.js";

export const TELTONIKA_CARD_PARAM_BASE = 30000;
export const TELTONIKA_CARD_SLOTS = 4000;
/** Card ids are 64-bit values (1-Wire iButton / RFID reader): 16 hex digits. */
export const TELTONIKA_CARD_HEX_LEN = 16;
/** Slots per setparam / getparam command: keeps every command well below the GPRS command length limit. */
export const TELTONIKA_SLOTS_PER_COMMAND = 10;

/**
 * Scenario "Output control" parameters (DOUT driven by the scenario) of the FMB device family, which the
 * FMC130 belongs to (Teltonika wiki: FMB Device Family Features / Accelerometer Features parameter lists).
 * 0 = None. FTS only READS them: a value other than 0 means the unit could act on the vehicle by itself, so a
 * device-health alarm is raised. They are never written. Crash detection (11400-11406) has no output control
 * parameter; the immobiliser (11702) is the intended DOUT1 user and is not checked.
 */
export const TELTONIKA_DOUT_CONTROL_PARAMS = {
  11003: "green_driving",
  11103: "overspeeding",
  11204: "excessive_idling",
  11304: "jamming",
} as const;
export type TeltonikaDoutScenario = (typeof TELTONIKA_DOUT_CONTROL_PARAMS)[keyof typeof TELTONIKA_DOUT_CONTROL_PARAMS];
export const TELTONIKA_DOUT_SCENARIOS = Object.values(TELTONIKA_DOUT_CONTROL_PARAMS) as TeltonikaDoutScenario[];

/** A slot as last confirmed. `uid` null = content unknown (a write that was never answered): must be rewritten. */
export type CardSlot = { slot: number; uid: string | null };
/** One slot write: `uid` null clears the slot (value 0). */
export type SlotChange = { slot: number; uid: string | null };

/**
 * IO 78 (iButton / RFID id, 8 bytes) -> FTS card UID: 16 upper-case hex digits, zero-padded, so it matches
 * operator_cards.uid (^[0-9A-F]{8,20}$) when the card is registered with the same 16 digits. 0 = no card.
 */
export function teltonikaCardUid(io78: bigint | undefined): string | null {
  if (io78 === undefined || io78 <= 0n || io78 >= 1n << 64n) return null;
  return io78.toString(16).toUpperCase().padStart(TELTONIKA_CARD_HEX_LEN, "0");
}

/**
 * Parameter value for a stored card UID: the same 16 hex digits (shorter UIDs zero-padded on the left, i.e.
 * the same number). Null when the UID cannot be a 64-bit id (more than 16 hex digits) or is not a valid UID.
 */
export function cardParamValue(uid: string): string | null {
  if (!cardUid.safeParse(uid).success || uid.length > TELTONIKA_CARD_HEX_LEN) return null;
  if (/^0+$/.test(uid)) return null;
  return uid.padStart(TELTONIKA_CARD_HEX_LEN, "0");
}

export type CardSyncPlan = {
  /** The slot state after every change is applied. */
  next: CardSlot[];
  /** Slot writes in slot order; reused slots are overwritten in one write (no clear first). */
  changes: SlotChange[];
  added: number;
  removed: number;
  /** Desired UIDs that cannot be stored on the unit (longer than 16 hex digits or invalid). */
  skipped: string[];
  /** More desired cards than slots: every removal is still planned, additions are capped (`dropped`). */
  overflow: boolean;
  dropped: number;
};

/**
 * Minimal set of slot writes that turns `current` (the last confirmed state) into a state holding exactly
 * the storable `desired` cards. Cards already on the unit keep their slot; slots of unknown content and freed
 * slots are reused first, then the lowest empty ones; an unknown slot that is not reused is cleared.
 * Removals always go through; when the cards do not fit, only additions are dropped (lowest ids kept).
 * Deterministic for the same input.
 */
export function planCardSync(current: CardSlot[], desired: string[], maxSlots = TELTONIKA_CARD_SLOTS): CardSyncPlan {
  const skipped: string[] = [];
  const want = new Set<string>();
  for (const uid of desired) {
    const v = cardParamValue(uid);
    if (v === null) skipped.push(uid);
    else want.add(v);
  }
  const held = new Map<number, string | null>(); // slot -> param value, null = unknown
  for (const s of current) {
    if (!Number.isInteger(s.slot) || s.slot < 0 || s.slot >= maxSlots) continue;
    if (s.uid === null) held.set(s.slot, null);
    else {
      const v = cardParamValue(s.uid);
      if (v !== null) held.set(s.slot, v);
    }
  }
  const keep = new Set<string>();
  const freed: number[] = [];
  for (const [slot, v] of [...held].sort((a, b) => a[0] - b[0])) {
    if (v !== null && want.has(v) && !keep.has(v)) keep.add(v);
    else freed.push(slot); // unknown, not wanted any more, or a duplicate of a kept card
  }
  const candidates = [...want].filter((v) => !keep.has(v)).sort();
  const room = maxSlots - keep.size;
  const toAdd = candidates.slice(0, Math.max(0, room));
  const dropped = candidates.length - toAdd.length;

  const writes = new Map<number, string | null>();
  const used = new Set(held.keys());
  let free = 0;
  const nextEmpty = () => {
    while (used.has(free)) free++;
    used.add(free);
    return free;
  };
  for (const v of toAdd) writes.set(freed.length ? freed.shift()! : nextEmpty(), v);
  for (const slot of freed) writes.set(slot, null);

  const state = new Map(held);
  for (const [slot, v] of writes) {
    if (v === null) state.delete(slot);
    else state.set(slot, v);
  }
  return {
    next: [...state].sort((a, b) => a[0] - b[0]).map(([slot, uid]) => ({ slot, uid })),
    changes: [...writes].sort((a, b) => a[0] - b[0]).map(([slot, uid]) => ({ slot, uid })),
    added: toAdd.length,
    removed: [...held.values()].filter((v) => v !== null && !want.has(v)).length,
    skipped,
    overflow: dropped > 0,
    dropped,
  };
}

const paramId = (slot: number) => TELTONIKA_CARD_PARAM_BASE + slot;

/** setparam text for a group of slot writes, e.g. "setparam 30000:0000A1B2C3D4E5F6;30001:0". */
export function setparamText(changes: SlotChange[]): string {
  if (changes.length === 0) throw new Error("no changes");
  return `setparam ${changes.map((c) => `${paramId(c.slot)}:${c.uid ?? "0"}`).join(";")}`;
}

/** Splits slot writes into setparam commands of at most `perCommand` slots, in slot order. */
export function buildCardCommands(changes: SlotChange[], perCommand = TELTONIKA_SLOTS_PER_COMMAND): { text: string; changes: SlotChange[] }[] {
  const out: { text: string; changes: SlotChange[] }[] = [];
  for (let i = 0; i < changes.length; i += perCommand) {
    const part = changes.slice(i, i + perCommand);
    const text = setparamText(part);
    assertCommandAllowed(text);
    out.push({ text, changes: part });
  }
  return out;
}

/** getparam commands reading every card slot, `perCommand` ids each ("getparam 30000;30001;…"). */
export function buildSlotReadCommands(slotCount = TELTONIKA_CARD_SLOTS, perCommand = TELTONIKA_SLOTS_PER_COMMAND): { text: string; ids: number[] }[] {
  const out: { text: string; ids: number[] }[] = [];
  for (let s = 0; s < slotCount; s += perCommand) {
    const ids = Array.from({ length: Math.min(perCommand, slotCount - s) }, (_, i) => paramId(s + i));
    const text = `getparam ${ids.join(";")}`;
    assertCommandAllowed(text);
    out.push({ text, ids });
  }
  return out;
}

/** The read-only safety check: the scenario output-control parameters. */
export function safetyReadCommand(): { text: string; ids: number[] } {
  const ids = Object.keys(TELTONIKA_DOUT_CONTROL_PARAMS).map(Number);
  const text = `getparam ${ids.join(";")}`;
  assertCommandAllowed(text);
  return { text, ids };
}

/**
 * Parameter id/value pairs in a unit's answer. Accepts the forms "New value 30000:<v>;30001:<v>",
 * "30000:<v>;30001:<v>" and "Param ID:30000 Value:<v>" (repeated). Values are trimmed, case kept.
 */
export function parseParamPairs(text: string): Map<number, string> {
  const out = new Map<number, string>();
  for (const m of text.matchAll(/Param\s*ID\s*:\s*(\d{1,5})\s*,?\s*(?:New\s+)?Value\s*:\s*([^\s;,]*)/gi)) out.set(Number(m[1]), m[2]!);
  if (out.size === 0) {
    const body = text.replace(/^\s*new\s+val(ue)?s?\s*:?\s*/i, "");
    for (const m of body.matchAll(/(?:^|[;\s])(\d{4,5}):([^;\s]*)/g)) out.set(Number(m[1]), m[2]!);
  }
  return out;
}

/**
 * Device answer to setparam. Success only when it says "New value" AND echoes exactly the parameter ids that
 * were sent with the values that were sent (so a late answer to an earlier command is never taken for this
 * one). Anything else (e.g. "Param ID consistency mismatch") is a failure.
 */
export function parseSetparamResponse(text: string, sent: SlotChange[]): { ok: boolean; matches: boolean; detail: string } {
  const t = text.trim();
  const detail = t.slice(0, 200);
  const pairs = parseParamPairs(t);
  const expected = new Map(sent.map((c) => [paramId(c.slot), c.uid ?? "0"]));
  const matches = pairs.size === expected.size && [...expected].every(([id, v]) => pairs.has(id) && pairs.get(id)!.toUpperCase() === v.toUpperCase());
  const failure = !/^new val(ue)?s?\b/i.test(t);
  return { ok: !failure && matches, matches: failure || matches, detail };
}

/** Whether a getparam answer carries every requested id (used to match answers to their command). */
export function getparamAnswerMatches(text: string, ids: number[]): boolean {
  const pairs = parseParamPairs(text);
  return ids.every((id) => pairs.has(id));
}

/**
 * Card slots read back from the unit. Value "0" or empty = empty slot; a 16-hex-digit value = a card; any
 * other value is unknown (kept as unknown, so the next delivery rewrites it).
 */
export function slotsFromAnswer(text: string, ids: number[]): CardSlot[] {
  const pairs = parseParamPairs(text);
  const out: CardSlot[] = [];
  for (const id of ids) {
    const v = (pairs.get(id) ?? "").toUpperCase();
    if (v === "" || /^0+$/.test(v)) continue;
    out.push({ slot: id - TELTONIKA_CARD_PARAM_BASE, uid: /^[0-9A-F]{1,16}$/.test(v) ? v.padStart(16, "0") : null });
  }
  return out;
}

/** Scenarios whose output control is not "None" in a safety-check answer; null when the answer is incomplete. */
export function unsafeScenarios(text: string): TeltonikaDoutScenario[] | null {
  const pairs = parseParamPairs(text);
  const out: TeltonikaDoutScenario[] = [];
  for (const [id, name] of Object.entries(TELTONIKA_DOUT_CONTROL_PARAMS)) {
    const v = pairs.get(Number(id));
    if (v === undefined) return null;
    if (v.trim() !== "0") out.push(name);
  }
  return out;
}

/** Read-only queries the gateway may send. */
const QUERY_COMMANDS = new Set(["getinfo", "getver", "getstatus", "getgps", "getio"]);
const SAFETY_READ_IDS = new Set(Object.keys(TELTONIKA_DOUT_CONTROL_PARAMS).map(Number));
const isCardParam = (id: number) => id >= TELTONIKA_CARD_PARAM_BASE && id < TELTONIKA_CARD_PARAM_BASE + TELTONIKA_CARD_SLOTS;

/**
 * The single gate every Codec 12 command passes before it is written to a socket. Allowed:
 *  - the read-only queries above;
 *  - `getparam` of card slots (30000..33999) and of exactly the scenario output-control ids (read only);
 *  - `setparam` whose every parameter is a card slot with a 16-hex-digit id or 0.
 * Everything else throws — `setdigout`, `setparam` of any other parameter (outputs, scenarios, crash
 * detection settings, the output-control ids themselves), resets, firmware updates.
 */
export function assertCommandAllowed(text: string): void {
  const t = text.trim();
  const [cmd, ...rest] = t.split(/\s+/);
  const name = (cmd ?? "").toLowerCase();
  if (QUERY_COMMANDS.has(name) && rest.length === 0) return;
  if ((name === "setparam" || name === "getparam") && rest.length === 1) {
    const items = rest[0]!.split(";").filter((p) => p !== "");
    const ok =
      items.length > 0 &&
      items.length <= 50 &&
      items.every((p) => {
        if (name === "getparam") {
          if (!/^\d{4,5}$/.test(p)) return false;
          const id = Number(p);
          return isCardParam(id) || SAFETY_READ_IDS.has(id);
        }
        const m = /^(\d{5}):(0|[0-9A-F]{16})$/.exec(p);
        return !!m && isCardParam(Number(m[1]));
      });
    if (ok) return;
  }
  throw new Error(`command not allowed: ${name || "(empty)"} (ADR 0003, ADR 0032)`);
}

export function isCommandAllowed(text: string): boolean {
  try {
    assertCommandAllowed(text);
    return true;
  } catch {
    return false;
  }
}
